import type { FormularyResponseT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { cn } from '@/lib/utils';
import { describeFormularyCoverage } from '../model/formularyDisplayPolicy';

export interface FormularyStatusBadgeProps {
  formulary: FormularyResponseT | null | undefined;
  isLoading: boolean;
  hasResponse: boolean;
}

/**
 * Clinician-facing summary of the plan response. Raw formulary codes and
 * restriction fields remain available in the plan-details disclosure.
 */
export function FormularyStatusBadge({
  formulary,
  isLoading,
  hasResponse,
}: FormularyStatusBadgeProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
        <span className="size-2 animate-pulse rounded-full bg-muted-foreground/60" aria-hidden />
        Checking the patient’s plan…
      </div>
    );
  }

  if (!hasResponse) {
    return null;
  }

  const message = describeFormularyCoverage(formulary?.FormularyStatus, formulary?.Restrictions);
  return (
    <div>
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium',
          message.tone === 'positive' && 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-200',
          message.tone === 'warning' && 'bg-amber-500/12 text-amber-700 dark:text-amber-200',
          message.tone === 'danger' && 'bg-red-500/12 text-red-700 dark:text-red-200',
          message.tone === 'neutral' && 'bg-muted text-muted-foreground'
        )}
        aria-live="polite"
      >
        <span
          className={cn(
            'size-2 rounded-full',
            message.tone === 'positive' && 'bg-emerald-500',
            message.tone === 'warning' && 'bg-amber-500',
            message.tone === 'danger' && 'bg-red-500',
            message.tone === 'neutral' && 'bg-muted-foreground'
          )}
          aria-hidden
        />
        {message.title}
      </div>
      <p className="mt-2 max-w-lg text-sm leading-5 text-muted-foreground">{message.description}</p>
    </div>
  );
}
